import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import Dropdown from '@lib/inputs/Dropdown';
import {
    AV_COMMENT_COLS,
    AV_COMMENT_ROWS,
    AV_EXTERNAL_COMMENT_MAX_LENGTH,
    AV_INTERNAL_COMMENT_MAX_LENGTH,
    AvDecision,
    avRejectReasonOptions,
} from '../../constants/ticketlist.constants';
import { IAutoVerificationResult } from '../../constants/ticketlist.interface';
import {
    StyledAvCardTop,
    StyledAvComment,
    StyledAvDivider,
    StyledAvField,
    StyledAvFieldset,
    StyledAvFormLabel,
    StyledAvLegend,
    StyledAvMutedCard,
    StyledAvNote,
    StyledAvPanelTitle,
    StyledAvRadioCard,
    StyledAvRadioRow,
    StyledCharacterRemaining,
    StyledInputContainer
} from '../../constants/ticketlist.styled';
import { handleChange, isErrorResult, isRejectResult } from '../../constants/ticketlist.utils';

interface IManualReviewPanelProps {
    form: UseFormReturn<any>;
    avResult: IAutoVerificationResult;
}

export const ManualReviewPanel = ({ form, avResult }: IManualReviewPanelProps) => {
    const intl = useIntl();
    const {
        register,
        watch,
        setValue,
    } = form;
    const values = watch();
    const decision = values.decision;
    const hasAvResult = !!avResult?.result?.trim();
    const isErrorAvResult = isErrorResult(avResult?.result);
    const avDecision = isRejectResult(avResult?.result) ? AvDecision.REJECTED : AvDecision.ACCEPTED;
    const showDecisionNotes = hasAvResult && !isErrorAvResult && !!decision;
    const showMatchNote = showDecisionNotes && decision === avDecision;
    const showMismatchNote = showDecisionNotes && decision !== avDecision;

    return (
        <StyledAvMutedCard data-testid="manual-review-panel">
            <StyledAvCardTop>
                <StyledAvPanelTitle>
                    <FormattedMessage id="ticket.autoVerification.manualReview.title" />
                </StyledAvPanelTitle>

                <StyledAvFieldset>
                    <StyledAvLegend>
                        <FormattedMessage id="ticket.autoVerification.manualReview.yourDecision" />
                        <span className="req"> *</span>
                    </StyledAvLegend>
                    <StyledAvRadioRow>
                        <StyledAvRadioCard $selected={decision === AvDecision.ACCEPTED} htmlFor="av-decision-accept">
                            <FormattedMessage id="ticket.autoVerification.decision.accept" />
                            <input
                                id="av-decision-accept"
                                type="radio"
                                value={AvDecision.ACCEPTED}
                                data-testid="av-decision-accept"
                                {...register('decision')}
                            />
                        </StyledAvRadioCard>
                        <StyledAvRadioCard $selected={decision === AvDecision.REJECTED} htmlFor="av-decision-reject">
                            <FormattedMessage id="ticket.autoVerification.decision.reject" />
                            <input
                                id="av-decision-reject"
                                type="radio"
                                value={AvDecision.REJECTED}
                                data-testid="av-decision-reject"
                                {...register('decision')}
                            />
                        </StyledAvRadioCard>
                    </StyledAvRadioRow>
                    <ConditionalRendering isTrue={showMismatchNote}>
                        <StyledAvNote $warn data-testid="av-decision-mismatch">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <FormattedMessage id="ticket.autoVerification.manualReview.decisionDiffers" />
                        </StyledAvNote>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={showMatchNote}>
                        <StyledAvNote data-testid="av-decision-match">
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <FormattedMessage id="ticket.autoVerification.manualReview.decisionMatches" />
                        </StyledAvNote>
                    </ConditionalRendering>
                </StyledAvFieldset>

                <ConditionalRendering isTrue={decision === AvDecision.REJECTED}>
                    <StyledAvField>
                        <Dropdown
                            label={{
                                text: <FormattedMessage id="ticket.autoVerification.card.reasonForReject" />,
                                labelClass: ['dropdown-label'],
                            }}
                            shouldParse
                            inputData={{
                                inputClass: ['dropdown-input'],
                                name: 'rejectReason',
                                type: INPUT_TYPES.DROPDOWN,
                                value: values.rejectReason,
                                isInputRequired: true,
                                options: avRejectReasonOptions,
                            }}
                            inputActions={{
                                handleFieldChange: value => handleChange('rejectReason', value, setValue),
                                onFieldBlur: () => {},
                            }}
                        />
                    </StyledAvField>
                </ConditionalRendering>
            </StyledAvCardTop>

            <StyledAvComment>
                <StyledAvDivider />
                <StyledInputContainer>
                    <StyledAvFormLabel htmlFor="avInternalComment" className="required-true">
                        <FormattedMessage id="ticket.autoVerification.manualReview.internalComment" />
                    </StyledAvFormLabel>
                    <textarea
                        id="avInternalComment"
                        aria-required
                        aria-describedby="av-internal-comment-count"
                        data-testid="av-internal-comment"
                        placeholder={intl.formatMessage({
                            id: 'ticket.autoVerification.manualReview.internalCommentPlaceholder',
                        })}
                        maxLength={AV_INTERNAL_COMMENT_MAX_LENGTH}
                        cols={AV_COMMENT_COLS}
                        rows={AV_COMMENT_ROWS}
                        {...register('internalComment')}
                    />
                    <StyledCharacterRemaining id="av-internal-comment-count" aria-live="polite">
                        <FormattedMessage
                            id="ticketList.characterLeft"
                            values={{ charLeft: AV_INTERNAL_COMMENT_MAX_LENGTH - (values.internalComment?.length || 0) }}
                        />
                    </StyledCharacterRemaining>
                </StyledInputContainer>
            </StyledAvComment>

            <StyledAvComment>
                <StyledAvDivider />
                <StyledInputContainer>
                    <StyledAvFormLabel htmlFor="avExternalComment">
                        <FormattedMessage id="ticket.autoVerification.manualReview.externalComment" />
                    </StyledAvFormLabel>
                    <textarea
                        id="avExternalComment"
                        aria-describedby="av-external-comment-count"
                        data-testid="av-external-comment"
                        placeholder={intl.formatMessage({
                            id: 'ticket.autoVerification.manualReview.externalCommentPlaceholder',
                        })}
                        maxLength={AV_EXTERNAL_COMMENT_MAX_LENGTH}
                        cols={AV_COMMENT_COLS}
                        rows={AV_COMMENT_ROWS}
                        {...register('externalComment')}
                    />
                    <StyledCharacterRemaining id="av-external-comment-count" aria-live="polite">
                        <FormattedMessage
                            id="ticketList.characterLeft"
                            values={{ charLeft: AV_EXTERNAL_COMMENT_MAX_LENGTH - (values.externalComment?.length || 0) }}
                        />
                    </StyledCharacterRemaining>
                </StyledInputContainer>
            </StyledAvComment>
        </StyledAvMutedCard>
    );
};
